import {
  type RemixiconComponentType,
  RiBox3Fill,
  RiCheckboxCircleFill,
  RiCheckboxCircleLine,
  RiCropLine,
  RiEditCircleFill,
  RiGridFill,
  RiInboxArchiveFill,
  RiRecordCircleFill,
  RiRouteFill,
  RiRuler2Fill,
  RiScissorsLine,
  RiStackFill,
  RiTruckFill,
  RiTruckLine
} from '@remixicon/react'

export const DETAIL_STATUSES = [
  'cutting',
  'cut',
  'punching',
  'punched',
  'bending',
  'forming',
  'varionbend',
  'siding',
  'siding_done',
  'trim',
  'trim_done',
  'soffit',
  'soffit_done',
  'packaging',
  'ready_for_pack',
  'complete',
  'ready_to_ship',
  'shipped'
] as const

export type DetailStatus = (typeof DETAIL_STATUSES)[number]

export const STATUS_GROUPS = ['not-started', 'active', 'completed'] as const

interface StatusGroupConfigItem {
  name: string | null
}

export type StatusGroup = 'not-started' | 'active' | 'completed'

export const STATUS_GROUPS_CONFIG: Record<StatusGroup, StatusGroupConfigItem> = {
  'not-started': {
    name: 'Not started'
  },
  active: {
    name: 'Active'
  },
  completed: {
    name: null
  }
}

export interface StatusConfigItem {
  icon: RemixiconComponentType
  name: string
  value: DetailStatus
  group: StatusGroup
  triggerClassName: string
  itemClassName: string
}

export const STATUS_CONFIG: Record<DetailStatus, StatusConfigItem> = {
  cutting: {
    icon: RiScissorsLine,
    name: 'Cutting',
    value: 'cutting',
    group: 'active',
    triggerClassName: 'bg-blue-100 [&_svg]:text-blue-200 text-black-200',
    itemClassName: '[&_svg]:text-blue-200'
  },
  cut: {
    icon: RiCropLine,
    name: 'Cut',
    value: 'cut',
    group: 'active',
    triggerClassName: 'bg-blue-200 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-blue-200'
  },
  punching: {
    icon: RiEditCircleFill,
    name: 'Punching',
    value: 'punching',
    group: 'active',
    triggerClassName: 'bg-red-100 [&_svg]:text-red-300 text-black-200',
    itemClassName: '[&_svg]:text-red-300'
  },
  punched: {
    icon: RiRecordCircleFill,
    name: 'Punched',
    value: 'punched',
    group: 'active',
    triggerClassName: 'bg-red-300 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-red-300'
  },
  bending: {
    icon: RiRouteFill,
    name: 'Bending',
    value: 'bending',
    group: 'active',
    triggerClassName: 'bg-yellow-100 [&_svg]:text-yellow-200 text-black-200',
    itemClassName: '[&_svg]:text-yellow-200'
  },
  forming: {
    icon: RiRouteFill,
    name: 'Forming',
    value: 'forming',
    group: 'active',
    triggerClassName: 'bg-yellow-100 [&_svg]:text-yellow-200 text-black-200',
    itemClassName: '[&_svg]:text-yellow-200'
  },
  varionbend: {
    icon: RiRouteFill,
    name: 'Variobend',
    value: 'varionbend',
    group: 'active',
    triggerClassName: 'bg-cyan-100 [&_svg]:text-cyan-200 text-black-200',
    itemClassName: '[&_svg]:text-cyan-200'
  },
  ready_for_pack: {
    icon: RiInboxArchiveFill,
    name: 'Ready for Pack',
    value: 'ready_for_pack',
    group: 'active',
    triggerClassName: 'bg-purple-100 [&_svg]:text-purple-200 text-black-200',
    itemClassName: '[&_svg]:text-purple-200'
  },
  packaging: {
    icon: RiBox3Fill,
    name: 'Packaging',
    value: 'packaging',
    group: 'active',
    triggerClassName: 'bg-purple-200 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-purple-200'
  },
  siding: {
    icon: RiStackFill,
    name: 'B&B Siding',
    value: 'siding',
    group: 'active',
    triggerClassName: 'bg-orange-100 [&_svg]:text-orange-300 text-black-200',
    itemClassName: '[&_svg]:text-orange-300'
  },
  siding_done: {
    icon: RiStackFill,
    name: 'Siding Done',
    value: 'siding_done',
    group: 'active',
    triggerClassName: 'bg-orange-300 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-orange-300'
  },
  trim: {
    icon: RiRuler2Fill,
    name: 'Trim Access',
    value: 'trim',
    group: 'active',
    triggerClassName: 'bg-blue-100 [&_svg]:text-blue-300 text-black-200',
    itemClassName: '[&_svg]:text-blue-300'
  },
  trim_done: {
    icon: RiRuler2Fill,
    name: 'Trim Done',
    value: 'trim_done',
    group: 'active',
    triggerClassName: 'bg-blue-300 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-blue-300'
  },
  soffit: {
    icon: RiGridFill,
    name: 'Soffit',
    value: 'soffit',
    group: 'active',
    triggerClassName: 'bg-purple-100 [&_svg]:text-purple-200 text-black-200',
    itemClassName: '[&_svg]:text-purple-200'
  },
  soffit_done: {
    icon: RiCheckboxCircleLine,
    name: 'Soffit Done',
    value: 'soffit_done',
    group: 'active',
    triggerClassName: 'bg-purple-200 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-purple-200'
  },
  complete: {
    icon: RiCheckboxCircleFill,
    name: 'Completed',
    value: 'complete',
    group: 'completed',
    triggerClassName: 'bg-green-100 [&_svg]:text-green-200 text-black-200',
    itemClassName: '[&_svg]:text-green-200'
  },
  ready_to_ship: {
    icon: RiTruckLine,
    name: 'Ready to Ship',
    value: 'ready_to_ship',
    group: 'completed',
    triggerClassName: 'bg-orange-100 [&_svg]:text-orange-200 text-black-200',
    itemClassName: '[&_svg]:text-orange-200'
  },
  shipped: {
    icon: RiTruckFill,
    name: 'Shipped',
    value: 'shipped',
    group: 'completed',
    triggerClassName: 'bg-purple-200 [&_svg]:text-white-100 text-white-100',
    itemClassName: '[&_svg]:text-white-100'
  }
}

export const getStatusesByGroup = (group: StatusGroup): DetailStatus[] => {
  return Object.entries(STATUS_CONFIG)
    .filter(([_, config]) => config.group === group)
    .map(([status]) => status as DetailStatus)
}

export const COMPLETED_DETAIL_STATUSES = ['complete', 'ready_to_ship', 'shipped'] as const satisfies readonly DetailStatus[]

const COMPLETED_DETAIL_STATUS_SET = new Set<DetailStatus>(COMPLETED_DETAIL_STATUSES)

export const isCompletedDetailStatus = (status: DetailStatus | null | undefined): boolean =>
  !!status && COMPLETED_DETAIL_STATUS_SET.has(status)
